import { useState, useEffect, useCallback } from 'react';
import { listCollaborators, shareProject, removeCollaborator } from '../api/sharing';
import { listMembers } from '../api/members';
import { VIEWER } from '../constants/roles';
import './ShareProjectModal.css';
import Button from './Button';

// Project sharing is read-only: recipients get VIEWER access only.

// Self-fetching share dialog (keeps ProjectPage/Dashboard from growing). Follows
// the FileRepository overlay + stop-propagation modal pattern.
export default function ShareProjectModal({ project, orgId, onClose }) {
  const projectId = project?.projectId;
  const [collaborators, setCollaborators] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pickUser, setPickUser] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        listCollaborators(projectId),
        orgId
          ? listMembers(orgId).catch(() => ({ members: [] }))
          : Promise.resolve({ members: [] }),
      ]);
      setCollaborators(c.collaborators || []);
      setMembers(m.members || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [projectId, orgId]);

  useEffect(() => {
    if (projectId) load();
  }, [projectId, load]);

  const emailFor = (userId) => members.find((m) => m.userId === userId)?.email || userId;
  const collaboratorIds = new Set(collaborators.map((c) => c.userId));
  const candidates = members.filter(
    (m) => !collaboratorIds.has(m.userId) && m.userId !== project?.ownerId
  );

  async function handleAdd() {
    if (!pickUser) return;
    setBusy(true);
    try {
      await shareProject(projectId, pickUser, VIEWER);
      setCollaborators((prev) => [
        ...prev.filter((c) => c.userId !== pickUser),
        { userId: pickUser, role: VIEWER },
      ]);
      setPickUser('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemove(userId) {
    try {
      await removeCollaborator(projectId, userId);
      setCollaborators((prev) => prev.filter((c) => c.userId !== userId));
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="share-overlay" onClick={onClose}>
      <div className="share-modal" onClick={(e) => e.stopPropagation()}>
        <div className="share-head">
          <h2>Share “{project?.name}”</h2>
          <button className="share-close" onClick={onClose}>
            ✕
          </button>
        </div>
        {error && (
          <div className="share-error" onClick={() => setError(null)}>
            {error} ✕
          </div>
        )}
        {loading ? (
          <p className="share-empty">Loading…</p>
        ) : (
          <>
            <div className="share-add">
              <select value={pickUser} onChange={(e) => setPickUser(e.target.value)}>
                <option value="">Add a team member…</option>
                {candidates.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.email || m.userId}
                  </option>
                ))}
              </select>
              <Button variant="primary" size="sm" onClick={handleAdd} disabled={!pickUser || busy}>
                Add
              </Button>
            </div>
            <p className="share-hint">
              Members you add get read-only access. They can view the project and make their own
              copy, but can't edit it.
            </p>
            {candidates.length === 0 && members.length > 0 && (
              <p className="share-hint">Everyone in your team already has access.</p>
            )}
            <div className="share-list">
              {collaborators.filter((c) => c.userId !== '*').length === 0 && (
                <p className="share-empty">Not shared with anyone yet.</p>
              )}
              {collaborators
                .filter((c) => c.userId !== '*')
                .map((c) => (
                  <div key={c.userId} className="share-item">
                    <span className="share-email">{emailFor(c.userId)}</span>
                    <span className="share-role">Read-only</span>
                    <Button variant="ghost" size="sm" onClick={() => handleRemove(c.userId)}>
                      Remove
                    </Button>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
